import { useState, useEffect, useRef, useCallback } from 'react';
import { APP_VERSION, CORPUS_STATS } from '../constants';
import styles from './Settings.module.css';
import type { UserPreferences, Jurisdiction } from '../types';
import { DEFAULT_PREFERENCES, DOMAIN_OPTIONS, JURISDICTION_OPTIONS } from '../types';
import { fetchPreferences, savePreferences, authFetch } from '../api';
import { useToast } from '../contexts/ToastContext';

/* ── B3: User Spend Types ─────────────────────────────────────── */
const API_BASE_SETTINGS = import.meta.env.VITE_API_URL || '';
interface UserSpendData {
  today: number;
  this_week: number;
  this_month: number;
  query_count: number;
}
function formatSpend(v: number): string {
  return `$${v.toFixed(2)}`;
}

/* ── Types ───────────────────────────────────────────────────── */

export type Theme = 'dark' | 'light' | 'system';
export type Skin  = 'synthesis' | 'industrial';

/* ── Color Utilities ─────────────────────────────────────────── */

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  s /= 100; l /= 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) =>
    l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [Math.round(f(0) * 255), Math.round(f(8) * 255), Math.round(f(4) * 255)];
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, Math.round(l * 100)];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return [Math.round(h * 60), Math.round(s * 100), Math.round(l * 100)];
}

function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
}

function hexToRgb(hex: string): [number, number, number] | null {
  const clean = hex.replace(/^#/, '');
  if (clean.length !== 6) return null;
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return null;
  return [r, g, b];
}

/* ── Accent Application ──────────────────────────────────────── */

export function applyAccent(h: number, s: number, l: number): void {
  const root = document.documentElement;
  root.style.setProperty('--accent', `hsl(${h} ${s}% ${l}%)`);
  root.style.setProperty('--accent-soft', `hsl(${h} ${s}% ${Math.min(l + 10, 88)}% / 0.15)`);
  root.style.setProperty('--accent-glow', `hsl(${h} ${s}% ${l}% / 0.35)`);
  // --accent-text: readable text on page/card backgrounds — theme-aware
  const isDark = root.getAttribute('data-theme') === 'dark' ||
    (!root.getAttribute('data-theme') && window.matchMedia('(prefers-color-scheme: dark)').matches);
  root.style.setProperty('--accent-text', isDark
    ? `hsl(${h} ${Math.min(s, 90)}% ${Math.min(l + 15, 85)}%)`
    : `hsl(${h} ${s}% ${Math.max(l - 15, 30)}%)`);
  // --accent-on-surface: alias (backwards compat)
  root.style.setProperty('--accent-on-surface', root.style.getPropertyValue('--accent-text'));
  // --accent-btn-text: contrast text for filled accent buttons
  root.style.setProperty('--accent-btn-text', l > 55 ? '#0f172a' : '#ffffff');
}

/* ── Presets ─────────────────────────────────────────────────── */

interface Preset { name: string; h: number; s: number; l: number; }

const PRESETS: Preset[] = [
  { name: 'Indigo', h: 239, s: 84, l: 67 },
  { name: 'Teal',   h: 174, s: 84, l: 67 },
  { name: 'Rose',   h: 340, s: 84, l: 67 },
  { name: 'Amber',  h: 38,  s: 92, l: 50 },
];

const DEFAULT_ACCENT = PRESETS[0]; // Indigo

function loadStoredAccent(): { h: number; s: number; l: number } {
  try {
    const h = parseInt(localStorage.getItem('ch-accent-h') ?? '');
    const s = parseInt(localStorage.getItem('ch-accent-s') ?? '');
    const l = parseInt(localStorage.getItem('ch-accent-l') ?? '');
    if (!isNaN(h) && !isNaN(s) && !isNaN(l)) return { h, s, l };
  } catch { /* ignore */ }
  return DEFAULT_ACCENT;
}

function matchPreset(h: number, s: number, l: number): number {
  return PRESETS.findIndex(p => p.h === h && p.s === s && p.l === l);
}

/* ── Gear Icon ───────────────────────────────────────────────── */

export function GearIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

/* ── Props ───────────────────────────────────────────────────── */

interface SettingsProps {
  isOpen: boolean;
  theme: Theme;
  onThemeChange: (theme: Theme) => void;
  skin: Skin;
  onSkinChange: (skin: Skin) => void;
  onClose: () => void;
  /** Phase 8: Callback when preferences change (domain filter / plain english) */
  onPreferencesChange?: (prefs: UserPreferences) => void;
}

/* ── Component ───────────────────────────────────────────────── */

export default function Settings({
  isOpen,
  theme,
  onThemeChange,
  skin,
  onSkinChange,
  onClose,
  onPreferencesChange,
}: SettingsProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const { addToast } = useToast();

  /* ── Phase 8: Preferences state ── */
  const [prefs, setPrefs] = useState<UserPreferences>(DEFAULT_PREFERENCES);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [prefsSaved, setPrefsSaved] = useState(false);
  const [prefsError, setPrefsError] = useState<string | null>(null);

  /* ── B3: User spend state ── */
  const [userSpend, setUserSpend] = useState<UserSpendData | null>(null);
  const [spendLoading, setSpendLoading] = useState(false);

  // Load preferences on mount
  useEffect(() => {
    let cancelled = false;
    fetchPreferences().then((loaded) => {
      if (!cancelled) {
        setPrefs(loaded);
        setPrefsLoaded(true);
        onPreferencesChange?.(loaded);
      }
    });
    return () => { cancelled = true; };
  }, []);

  // B3: Load user spend when settings opens
  useEffect(() => {
    if (!isOpen) return;
    if (localStorage.getItem('standards-user-role') === 'admin') return; // admins see cost dashboard
    setSpendLoading(true);
    authFetch(`${API_BASE_SETTINGS}/api/user/spend`)
      .then(r => r.ok ? r.json() as Promise<UserSpendData> : Promise.reject(new Error('Failed')))
      .then(d => setUserSpend(d))
      .catch(() => setUserSpend(null))
      .finally(() => setSpendLoading(false));
  }, [isOpen]);

  const handlePrefChange = useCallback(<K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => {
    setPrefs(prev => {
      const updated = { ...prev, [key]: value };
      return updated;
    });
    setPrefsSaved(false);
  }, []);

  const handleDomainToggle = useCallback((slug: string) => {
    setPrefs(prev => {
      const domains = prev.default_domains.includes(slug)
        ? prev.default_domains.filter(d => d !== slug)
        : [...prev.default_domains, slug];
      return { ...prev, default_domains: domains };
    });
    setPrefsSaved(false);
  }, []);

  const handleSavePrefs = useCallback(async () => {
    setPrefsSaving(true);
    setPrefsError(null);
    try {
      const saved = await savePreferences(prefs);
      setPrefs(saved);
      setPrefsSaved(true);
      onPreferencesChange?.(saved);
      setTimeout(() => setPrefsSaved(false), 2000);
      addToast('success', 'Settings saved', 2000);
    } catch (e) {
      setPrefsError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setPrefsSaving(false);
    }
  }, [prefs, onPreferencesChange, addToast]);

  /* ── Accent state (initialised from localStorage) ── */
  const stored = loadStoredAccent();
  const [accentH, setAccentH] = useState<number>(stored.h);
  const [accentS, setAccentS] = useState<number>(stored.s);
  const [accentL, setAccentL] = useState<number>(stored.l);
  const [rgb, setRgb] = useState<[number, number, number]>(() => hslToRgb(stored.h, stored.s, stored.l));
  const [hexInput, setHexInput] = useState<string>(() => rgbToHex(...hslToRgb(stored.h, stored.s, stored.l)));
  const [activePreset, setActivePreset] = useState<number>(() => matchPreset(stored.h, stored.s, stored.l));
  const [showCustom, setShowCustom] = useState<boolean>(() => matchPreset(stored.h, stored.s, stored.l) === -1);

  /* ── Escape to close ── */
  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  /* ── Apply accent when HSL changes ── */
  useEffect(() => {
    applyAccent(accentH, accentS, accentL);
    try {
      localStorage.setItem('ch-accent-h', String(accentH));
      localStorage.setItem('ch-accent-s', String(accentS));
      localStorage.setItem('ch-accent-l', String(accentL));
    } catch { /* ignore */ }
  }, [accentH, accentS, accentL]);

  /* ── Update helpers ── */
  const updateFromHsl = useCallback((h: number, s: number, l: number) => {
    setAccentH(h); setAccentS(s); setAccentL(l);
    const newRgb = hslToRgb(h, s, l);
    setRgb(newRgb);
    setHexInput(rgbToHex(...newRgb));
  }, []);

  const updateFromRgb = useCallback((r: number, g: number, b: number) => {
    setRgb([r, g, b]);
    const [h, s, l] = rgbToHsl(r, g, b);
    setAccentH(h); setAccentS(s); setAccentL(l);
    setHexInput(rgbToHex(r, g, b));
    setActivePreset(matchPreset(h, s, l));
  }, []);

  const handleHexCommit = useCallback((val: string) => {
    const result = hexToRgb(val);
    if (!result) return;
    const [r, g, b] = result;
    setRgb([r, g, b]);
    const [h, s, l] = rgbToHsl(r, g, b);
    setAccentH(h); setAccentS(s); setAccentL(l);
    setActivePreset(matchPreset(h, s, l));
  }, []);

  const selectPreset = useCallback((idx: number) => {
    const p = PRESETS[idx];
    setActivePreset(idx);
    setShowCustom(false);
    updateFromHsl(p.h, p.s, p.l);
  }, [updateFromHsl]);

  const previewColor = `hsl(${accentH} ${accentS}% ${accentL}%)`;

  /* ── Render ── */
  return (
    <>
      {/* Backdrop */}
      <div
        className={`${styles.backdrop} ${isOpen ? styles.backdropVisible : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Sidebar panel */}
      <div
        ref={panelRef}
        className={`${styles.panel} ${isOpen ? styles.panelOpen : ''}`}
        role="dialog"
        aria-label="Settings"
        aria-hidden={!isOpen}
      >
        {/* Close button */}
        <button className={styles.closeBtn} onClick={onClose} aria-label="Close settings">✕</button>

        {/* ═══════════════════════════════════════════════════════
            Section 1: APPEARANCE
            ═══════════════════════════════════════════════════════ */}
        <h3 className={styles.sectionTitle}>Appearance</h3>

        {/* Theme */}
        <section className={styles.section}>
          <div className={styles.segmented}>
            {(['dark', 'light', 'system'] as Theme[]).map((t) => (
              <button
                key={t}
                className={`${styles.segBtn} ${theme === t ? styles.segBtnActive : ''}`}
                onClick={() => onThemeChange(t)}
                type="button"
              >
                {t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
        </section>

        {/* Skin */}
        <section className={styles.section}>
          <div className={styles.segmented}>
            {([
              { id: 'synthesis' as Skin, label: 'Synthesis' },
              { id: 'industrial' as Skin, label: 'Industrial' },
            ]).map(({ id, label }) => (
              <button
                key={id}
                className={`${styles.segBtn} ${skin === id ? styles.segBtnActive : ''}`}
                onClick={() => onSkinChange(id)}
                type="button"
              >
                {label}
              </button>
            ))}
          </div>
        </section>

        {/* Accent Colour */}
        <section className={styles.section}>
          {/* Preset swatches */}
          <div className={styles.swatches}>
            {PRESETS.map((p, i) => (
              <button
                key={p.name}
                className={`${styles.swatch} ${activePreset === i ? styles.swatchActive : ''}`}
                style={{ background: `hsl(${p.h} ${p.s}% ${p.l}%)` }}
                onClick={() => selectPreset(i)}
                type="button"
                aria-label={p.name}
                title={p.name}
              />
            ))}
            {/* Custom swatch / toggle */}
            <button
              className={`${styles.swatch} ${styles.swatchCustomTrigger} ${(activePreset === -1 && showCustom) ? styles.swatchActive : ''}`}
              style={showCustom ? { background: previewColor } : undefined}
              onClick={() => { setShowCustom(v => !v); if (!showCustom) setActivePreset(-1); }}
              type="button"
              aria-label="Custom colour"
              title="Custom"
              aria-expanded={showCustom}
            >
              {!showCustom && <span className={styles.customPlus}>+</span>}
            </button>
          </div>

          {/* Custom colour editor */}
          {showCustom && (
            <div className={styles.customPanel}>
              {/* Hex + preview */}
              <div className={styles.previewRow}>
                <div
                  className={styles.previewSwatch}
                  style={{ background: previewColor }}
                  aria-hidden="true"
                />
                <input
                  type="text"
                  value={hexInput}
                  onChange={e => setHexInput(e.target.value)}
                  onBlur={e => handleHexCommit(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleHexCommit(hexInput)}
                  className={styles.hexInput}
                  placeholder="#6366f1"
                  maxLength={7}
                  spellCheck={false}
                  aria-label="Hex colour"
                />
              </div>

              {/* RGB sliders */}
              <div className={styles.sliderBlock}>
                <div className={styles.sliderBlockLabel}>RGB</div>
                {(['R', 'G', 'B'] as const).map((ch, i) => {
                  const val = rgb[i];
                  const onSliderChange = (v: number) => {
                    const next: [number, number, number] = [...rgb] as [number, number, number];
                    next[i] = v;
                    updateFromRgb(...next);
                  };
                  return (
                    <div key={ch} className={styles.sliderRow}>
                      <span className={styles.sliderLabel}>{ch}</span>
                      <input
                        type="range"
                        min={0}
                        max={255}
                        value={val}
                        onChange={e => onSliderChange(Number(e.target.value))}
                        className={styles.slider}
                        aria-label={`${ch} channel`}
                      />
                      <input
                        type="number"
                        min={0}
                        max={255}
                        value={val}
                        onChange={e => onSliderChange(Math.max(0, Math.min(255, Number(e.target.value))))}
                        className={styles.numInput}
                        aria-label={`${ch} value`}
                      />
                    </div>
                  );
                })}
              </div>

              {/* HSL sliders */}
              <div className={styles.sliderBlock}>
                <div className={styles.sliderBlockLabel}>HSL</div>
                {([
                  { label: 'H', val: accentH, max: 360, set: (v: number) => { setActivePreset(-1); updateFromHsl(v, accentS, accentL); } },
                  { label: 'S', val: accentS, max: 100, set: (v: number) => { setActivePreset(-1); updateFromHsl(accentH, v, accentL); } },
                  { label: 'L', val: accentL, max: 100, set: (v: number) => { setActivePreset(-1); updateFromHsl(accentH, accentS, v); } },
                ]).map(({ label, val, max, set }) => (
                  <div key={label} className={styles.sliderRow}>
                    <span className={styles.sliderLabel}>{label}</span>
                    <input
                      type="range"
                      min={0}
                      max={max}
                      value={val}
                      onChange={e => set(Number(e.target.value))}
                      className={styles.slider}
                      aria-label={`${label} channel`}
                    />
                    <input
                      type="number"
                      min={0}
                      max={max}
                      value={val}
                      onChange={e => set(Math.max(0, Math.min(max, Number(e.target.value))))}
                      className={styles.numInput}
                      aria-label={`${label} value`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* ═══════════════════════════════════════════════════════
            Section 2: QUERY DEFAULTS
            ═══════════════════════════════════════════════════════ */}
        <h3 className={styles.sectionTitle}>Query Defaults</h3>

        <section className={styles.section}>
          {/* Jurisdiction */}
          <div className={styles.fieldRow}>
            <label className={styles.fieldLabel}>Default Jurisdiction</label>
            <select
              className={styles.selectInput}
              value={prefs.default_jurisdiction}
              onChange={(e) => handlePrefChange('default_jurisdiction', e.target.value as Jurisdiction)}
            >
              {JURISDICTION_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          {/* Domain Filters */}
          <div className={styles.fieldRow} style={{ marginTop: '10px' }}>
            <label className={styles.fieldLabel}>Default Domains</label>
            <div className={styles.checkboxGrid}>
              {DOMAIN_OPTIONS.map(({ slug, label }) => (
                <label key={slug} className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={prefs.default_domains.includes(slug)}
                    onChange={() => handleDomainToggle(slug)}
                    className={styles.checkbox}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
            {prefs.default_domains.length === 0 && (
              <span className={styles.hint}>All domains (no filter)</span>
            )}
          </div>

          {/* Plain English toggle — moved here from Display per spec */}
          <label className={styles.toggleRow} style={{ marginTop: '10px' }}>
            <span>Plain English mode</span>
            <input
              type="checkbox"
              checked={prefs.plain_english}
              onChange={(e) => handlePrefChange('plain_english', e.target.checked)}
              className={styles.toggleCheckbox}
            />
            <span className={styles.toggleTrack}>
              <span className={styles.toggleThumb} />
            </span>
          </label>
        </section>

        {/* ═══════════════════════════════════════════════════════
            Section 3: DISPLAY
            ═══════════════════════════════════════════════════════ */}
        <h3 className={styles.sectionTitle}>Display</h3>

        <section className={styles.section}>
          <label className={styles.toggleRow}>
            <span>Show usage stats</span>
            <input
              type="checkbox"
              checked={prefs.show_usage}
              onChange={(e) => handlePrefChange('show_usage', e.target.checked)}
              className={styles.toggleCheckbox}
            />
            <span className={styles.toggleTrack}>
              <span className={styles.toggleThumb} />
            </span>
          </label>
        </section>

        {/* ═══════════════════════════════════════════════════════
            Section 3b: YOUR USAGE (B3 — non-admin users only)
            ═══════════════════════════════════════════════════════ */}
        {localStorage.getItem('standards-user-role') !== 'admin' && (
          <>
            <h3 className={styles.sectionTitle}>Your Usage</h3>
            <section className={styles.section}>
              {spendLoading ? (
                <div className={styles.hint}>Loading usage…</div>
              ) : userSpend ? (
                <>
                  <div className={styles.aboutRow}>
                    <span>Today</span>
                    <span className={styles.aboutValue}>{formatSpend(userSpend.today)}</span>
                  </div>
                  <div className={styles.aboutRow}>
                    <span>This week</span>
                    <span className={styles.aboutValue}>{formatSpend(userSpend.this_week)}</span>
                  </div>
                  <div className={styles.aboutRow}>
                    <span>This month</span>
                    <span className={styles.aboutValue}>{formatSpend(userSpend.this_month)}</span>
                  </div>
                  <div className={styles.aboutRow}>
                    <span>Total queries</span>
                    <span className={styles.aboutValue}>{userSpend.query_count.toLocaleString()}</span>
                  </div>
                  <p className={styles.hint} style={{ marginTop: '4px', fontSize: '0.7rem' }}>
                    Daily limit: $20.00
                  </p>
                </>
              ) : (
                <div className={styles.hint}>Usage data unavailable</div>
              )}
            </section>
          </>
        )}

        {/* ═══════════════════════════════════════════════════════
            Section 4: RESOURCES
            ═══════════════════════════════════════════════════════ */}
        <h3 className={styles.sectionTitle}>Resources</h3>

        <section className={styles.section}>
          <a
            href="/user-guide.html"
            className={styles.resourceLink}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" /></svg>
            Getting Started Guide
            <span className={styles.externalIcon}>↗</span>
          </a>
          <p className={styles.scopeNote}>
            This tool searches Australian, NZ, and international building &amp; product compliance standards. It won't provide legal advice, certify compliance, or answer off-topic questions.
          </p>
        </section>

        {/* ═══════════════════════════════════════════════════════
            Section 4b: ADMIN LINKS (admin-only)
            ═══════════════════════════════════════════════════════ */}
        {localStorage.getItem('standards-user-role') === 'admin' && (
          <>
            <h3 className={styles.sectionTitle}>Admin</h3>
            <section className={styles.section}>
              <a
                href="/admin/phase2.html"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.resourceLink}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" /></svg>
                Phase 2 Plan
                <span className={styles.externalIcon}>↗</span>
              </a>
            </section>
          </>
        )}

        {/* ═══════════════════════════════════════════════════════
            Section 5: ABOUT
            ═══════════════════════════════════════════════════════ */}
        <h3 className={styles.sectionTitle}>About</h3>

        <section className={styles.section}>
          <div className={styles.aboutRow}>
            <span>Version</span>
            <span className={styles.aboutValue}>v{APP_VERSION}</span>
          </div>
          <div className={styles.aboutRow}>
            <span>Source PDFs</span>
            <span className={styles.aboutValue}>{CORPUS_STATS.pdfs}</span>
          </div>
          <div className={styles.aboutRow}>
            <span>Clause files</span>
            <span className={styles.aboutValue}>{CORPUS_STATS.clauses.toLocaleString()}</span>
          </div>
        </section>

        {/* ── Save Button ── */}
        <section className={styles.section} style={{ marginTop: '8px' }}>
          <button
            className={styles.saveBtn}
            onClick={handleSavePrefs}
            disabled={prefsSaving}
            type="button"
          >
            {prefsSaving ? 'Saving…' : prefsSaved ? '✓ Saved' : 'Save Preferences'}
          </button>
          {prefsError && (
            <div className={styles.errorText}>{prefsError}</div>
          )}
          {!prefsLoaded && (
            <div className={styles.hint}>Loading preferences…</div>
          )}
        </section>
      </div>
    </>
  );
}
